import {useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import Button from 'components/ui/Button'
import TextField from 'components/ui/TextField'
import PhoneField from 'components/ui/PhoneField'
import * as Styles from 'layouts/PrivateLayout.styles'
import {SelectChangeEvent} from '@mui/material/Select'
import SingleSelect from 'components/ui/Select/SingleSelect'
import {statesSelectOptions} from '../locations/constants'
import {ICustomer, useUpdateCustomerMutation} from 'services/customers'
import {enqueueSnackbar} from "notistack";

interface ICustomerForm {
    customer?: ICustomer | any
    editCustomer?: boolean
}

const CustomerFormModule: React.FC<ICustomerForm> = ({
                                                         customer,
                                                         editCustomer = false,
                                                     }) => {

    let stateId = ''
    if (customer) {
        const filteredState = statesSelectOptions.filter((stateData) => stateData.label === customer.state)
        stateId = filteredState[0].id
    }
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState(customer?.first_name ?? '')
    const [lastName, setLastName] = useState(customer?.last_name ?? '')
    const [email, setEmail] = useState(customer?.email ?? '')
    const [phone, setPhone] = useState(customer?.phone ?? '')
    const [firstAddress, setFirstAddress] = useState(customer?.address_1 ?? '')
    const [secondAddress, setSecondAddress] = useState(customer?.address_2 ?? '')
    const [city, setCity] = useState(customer?.city ?? '')
    const [customerState, setCustomerState] = useState(stateId ?? '')
    const [zipCode, setZipCode] = useState(customer?.zip_code ?? '')
    const [updateCustomer] = useUpdateCustomerMutation();

    const disableSubmit = !firstName.length || !lastName.length || !email.length || !firstAddress.length || !zipCode.length || !city.length || !customerState.length

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const formData = new FormData(event.currentTarget)
        const formDataObject: any = Object.fromEntries(formData.entries())

        if (editCustomer) {
            handleEditCustomer(formDataObject)
        } else {
            handleCreateCustomer(formDataObject)
        }
    }

    const handleEditCustomer = async (formData: any) => {
        try {
            if(customer) {
                const filteredState = statesSelectOptions.filter((stateData) => stateData.id === formData.customerState)
                const data = {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.customerEmail,
                    phone: formData.phoneNumber,
                    address_1: formData.address1,
                    address_2: formData.address2,
                    city: formData.city,
                    state: filteredState[0].label,
                    zip_code: formData.zipCode,

                }
                const customerId = customer.id
                await updateCustomer({ customerId, body: data});

                enqueueSnackbar('Customer successfully updated', { variant: 'success', autoHideDuration: 1000 });
            } else {
                console.log("there is no customer to update");
            }
        } catch (error) {
            enqueueSnackbar('Failed to update customer. Please try again.', { variant: 'error', autoHideDuration: 1000 });
        }
    }


    const handleCreateCustomer = (formDataObject: any) => {
        navigate('/admin/customers')
    }


    return (
        <>
            <form onSubmit={handleSubmit}>
                <Styles.FormColumns border-type="border">
                    <Styles.FormColumnLeft>
                        <Styles.FormColumnLeftDescription><strong>Customer
                            Name*</strong></Styles.FormColumnLeftDescription>
                    </Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <TextField id='firstName' cc-width='full' value={firstName} required label="First name"
                                   onChange={e => setFirstName(e.target.value)}></TextField>
                        <TextField id='lastName' cc-width='full' value={lastName} required label="Last name"
                                   onChange={e => setLastName(e.target.value)}></TextField>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>
                <Styles.FormColumns>
                    <Styles.FormColumnLeft>
                        <Styles.FormColumnLeftDescription><strong>Email*</strong></Styles.FormColumnLeftDescription>
                    </Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <TextField id='customerEmail' value={email} required cc-width='full' label="Customer email"
                                   onChange={e => setEmail(e.target.value)}></TextField>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>
                <Styles.FormColumns border-type="border">
                    <Styles.FormColumnLeft>
                        <Styles.FormColumnLeftDescription><strong>Phone
                            number*</strong></Styles.FormColumnLeftDescription>
                    </Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <PhoneField
                            id='phoneNumber'
                            label=""
                            required
                            value={phone}
                            onChange={e => setPhone(e)}
                        ></PhoneField>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>
                <Styles.FormColumns>
                    <Styles.FormColumnLeft>
                        <Styles.FormColumnLeftDescription><strong>Address*</strong></Styles.FormColumnLeftDescription>
                    </Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <TextField id='address1' value={firstAddress} required cc-width='full' label="Address 1"
                                   onChange={e => setFirstAddress(e.target.value)}></TextField>
                        <TextField id='address2' value={secondAddress} cc-width='full' label="Address 2"
                                   onChange={e => setSecondAddress(e.target.value)}></TextField>
                        <Styles.SectionThreeColumns>
                            <TextField
                                id='zipCode'
                                required
                                type="number"
                                cc-width='full'
                                label="Zip code"
                                value={zipCode}
                                onChange={e => setZipCode(e.target.value)}
                            ></TextField>
                            <TextField id='city' value={city} required cc-width='full' label="City"
                                       onChange={e => setCity(e.target.value)}></TextField>
                            <SingleSelect
                                id='customerState'
                                required
                                label={'State'}
                                value={customerState}
                                onChange={(event: SelectChangeEvent<any>) =>
                                    setCustomerState(event.target.value)
                                }
                                options={[
                                    {id: '', label: 'Select State'},
                                    ...statesSelectOptions
                                ]}
                            ></SingleSelect>
                        </Styles.SectionThreeColumns>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>

                <Styles.FormColumns>
                    <Styles.FormColumnLeft/>
                    <Styles.FormColumnRight>
                        <Styles.FormColumnsFooterButtons>
                            <Link to={'/admin/customers'}>
                                <Button>Cancel</Button>
                            </Link>
                            <Button cc-color='purple' type='submit' disabled={disableSubmit}>Save</Button>
                        </Styles.FormColumnsFooterButtons>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>
            </form>
        </>
    )
}

export default CustomerFormModule
